// Warehouse-local date/time helpers for the inbound-appointment surfaces.
//
// The API contract fixes all defaults in warehouse-local terms (board date,
// override ranges, extemporáneo "today"). Never derive them from the
// browser's local date parts — an operator in BR viewing an MX warehouse
// would compute the wrong day. Same Intl approach as getTimeInTimezone in
// src/pages/CubboLastmile/utils.js.

// One Intl.DateTimeFormat per zone+options, reused across calls (~20x/render
// on the board). Never cached: a failed construction (invalid zone) or the
// browser-local fallback, which must follow the device's current zone.
const formatterCache = new Map()
const partsGetter = (timeZone, date, options) => {
  let formatter
  if (timeZone) {
    const key = `${timeZone}|${JSON.stringify(options)}`
    formatter = formatterCache.get(key)
    if (!formatter) {
      formatter = new Intl.DateTimeFormat('en-CA', { timeZone, ...options })
      formatterCache.set(key, formatter)
    }
  } else {
    formatter = new Intl.DateTimeFormat('en-CA', options)
  }
  const parts = formatter.formatToParts(date)
  return type => parts.find(p => p.type === type)?.value
}

const DATE_PARTS = { year: 'numeric', month: '2-digit', day: '2-digit' }
const TIME_PARTS = { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }

// 'YYYY-MM-DD' of the warehouse's current day. Falls back to browser-local
// when the timezone is missing or invalid.
export const todayInTimezone = (timeZone, now = new Date()) => {
  let get
  try {
    get = partsGetter(timeZone, now, DATE_PARTS)
  } catch {
    get = partsGetter(null, now, DATE_PARTS)
  }
  return `${get('year')}-${get('month')}-${get('day')}`
}

// 'YYYY-MM-DDTHH:mm' (a datetime-local input value) of the warehouse's "now".
export const nowForDatetimeInput = (timeZone, now = new Date()) => {
  let get
  try {
    get = partsGetter(timeZone, now, { ...DATE_PARTS, ...TIME_PARTS })
  } catch {
    get = partsGetter(null, now, { ...DATE_PARTS, ...TIME_PARTS })
  }
  return `${get('year')}-${get('month')}-${get('day')}T${get('hour')}:${get('minute')}`
}

// '±HH:MM' UTC offset of an IANA timezone at a given instant (DST-aware).
// Computed numerically (tz-localized parts read back as UTC, diffed against
// the instant) rather than via `timeZoneName: 'shortOffset'`, which throws on
// pre-2021 browsers (Chrome <95, Safari <15.4, Firefox <91) — warehouse kiosk
// hardware — and would silently stamp wall-clock times as UTC via the catch.
export const getUtcOffset = (timeZone, atDate = new Date()) => {
  try {
    const get = partsGetter(timeZone, atDate, { ...DATE_PARTS, ...TIME_PARTS, second: '2-digit' })
    const asUtc = Date.UTC(+get('year'), +get('month') - 1, +get('day'), +get('hour'), +get('minute'), +get('second'))
    const offsetMinutes = Math.round((asUtc - atDate.getTime()) / 60000)
    const sign = offsetMinutes < 0 ? '-' : '+'
    const abs = Math.abs(offsetMinutes)
    return `${sign}${String(Math.floor(abs / 60)).padStart(2, '0')}:${String(abs % 60).padStart(2, '0')}`
  } catch {
    return '+00:00'
  }
}

// Interprets a datetime-local value ('YYYY-MM-DDTHH:mm') as warehouse
// wall-clock time and returns ISO8601 with the warehouse's UTC offset. The
// offset is resolved near that instant, so edits within the API's ±2 day
// unload window carry the right offset even across a DST boundary.
export const wallClockToIso = (localValue, timeZone) => {
  const approxInstant = new Date(`${localValue}:00Z`)
  return `${localValue}:00${getUtcOffset(timeZone, approxInstant)}`
}

// 'YYYY-MM-DDTHH:mm' (a datetime-local input value) of an ISO instant in the
// warehouse timezone — the inverse of wallClockToIso, for prefilling inputs
// from the server-echoed unload fields (D10.1).
export const isoToWallClock = (isoString, timeZone) => {
  if (!isoString) return null
  const date = new Date(isoString)
  if (isNaN(date.getTime())) return null
  let get
  try {
    get = partsGetter(timeZone, date, { ...DATE_PARTS, ...TIME_PARTS })
  } catch {
    get = partsGetter(null, date, { ...DATE_PARTS, ...TIME_PARTS })
  }
  return `${get('year')}-${get('month')}-${get('day')}T${get('hour')}:${get('minute')}`
}

// 'HH:mm' wall-clock rendering of an ISO timestamp in the warehouse timezone
// (board check-in column).
export const formatTimeInTimezone = (isoString, timeZone) => {
  if (!isoString) return null
  const date = new Date(isoString)
  // An unparseable timestamp would throw from the browser-local FALLBACK
  // formatter too (outside the try) and crash the board render.
  if (isNaN(date.getTime())) return null
  let get
  try {
    get = partsGetter(timeZone, date, TIME_PARTS)
  } catch {
    get = partsGetter(null, date, TIME_PARTS)
  }
  return `${get('hour')}:${get('minute')}`
}

// 'YYYY-MM-DD' of the warehouse-local day an ISO instant fell on. Strict, unlike
// the display helpers above: null on a missing/invalid/offset-less instant OR
// timezone, because callers classify rows on it and a browser-local guess would move them.
export const dateInTimezone = (isoString, timeZone) => {
  if (!isoString || !timeZone || !/(Z|[+-]\d{2}:?\d{2})$/i.test(isoString)) return null
  const date = new Date(isoString)
  if (isNaN(date.getTime())) return null
  try {
    const get = partsGetter(timeZone, date, DATE_PARTS)
    return `${get('year')}-${get('month')}-${get('day')}`
  } catch {
    return null
  }
}

// A 'YYYY-MM-DD' calendar date ('21 sep', 'lun 17'): parsed and formatted in
// UTC so no browser timezone can shift the day. Returns null on a bad input.
export const formatCalendarDate = (dateStr, locale, options) => {
  if (typeof dateStr !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return null
  const date = new Date(`${dateStr}T00:00:00Z`)
  if (isNaN(date.getTime())) return null
  // An impossible calendar date (e.g. Feb 30) still parses — V8 rolls it over
  // to a real date — so re-derive the ISO date and require it to round-trip.
  if (date.toISOString().slice(0, 10) !== dateStr) return null
  return new Intl.DateTimeFormat(locale, { ...options, timeZone: 'UTC' }).format(date)
}

const MIN_YEAR = 1000

// True for a 'YYYY-MM-DD' string naming a real calendar date from the year 1000 on. Date.UTC rolls an impossible
// date over, so the parts are compared back; years below 1000 are refused as no date this app handles.
export const isRealDate = value => {
  const match = typeof value === 'string' ? /^(\d{4})-(\d{2})-(\d{2})$/.exec(value) : null
  if (!match) return false
  const [year, month, day] = match.slice(1).map(Number)
  if (year < MIN_YEAR) return false
  const date = new Date(Date.UTC(year, month - 1, day))
  return date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day
}

// 'YYYY-MM-DD' + n days, as pure calendar math (no timezone involved).
export const addDaysToDateString = (dateStr, days) => {
  const [y, m, d] = dateStr.split('-').map(Number)
  return new Date(Date.UTC(y, m - 1, d + days)).toISOString().slice(0, 10)
}

// The week-overview window: startDate plus the next (days - 1) dates.
export const weekDatesFrom = (startDate, days = 7) => Array.from({ length: days }, (_, index) => addDaysToDateString(startDate, index))
